/**
 * Serviço de Áreas da Empresa
 * Gerencia operações de API para áreas da empresa
 * 
 * @author Equipe GestaoeCRM
 * @date 31/01/2025
 */

import api from './api';

export interface CompanyArea {
  id: string;
  name: string;
  description?: string;
  managerId?: string;
  tenantId: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  manager?: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  users?: Array<{
    id: string;
    name: string;
    email: string;
    role: string;
    active: boolean;
  }>;
  _count?: {
    users: number;
  };
}

export interface CreateAreaData {
  name: string;
  description?: string;
  managerId?: string;
}

export interface UpdateAreaData {
  name?: string;
  description?: string;
  managerId?: string;
  active?: boolean;
}

export interface AreaFilters {
  active?: boolean;
  managerId?: string;
  search?: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface AvailableManager {
  id: string;
  name: string;
  email: string;
  role: string;
}

class CompanyAreaService {
  /**
   * Lista todas as áreas da empresa
   */
  async getAreas(filters?: AreaFilters & { page?: number; limit?: number }): Promise<{ data: CompanyArea[]; pagination: Pagination }>
  {
    const params = new URLSearchParams();
    
    if (filters?.active !== undefined) {
      params.append('active', filters.active.toString());
    }
    
    if (filters?.managerId) {
      params.append('managerId', filters.managerId);
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }
    params.append('page', String(filters?.page ?? 1));
    params.append('limit', String(filters?.limit ?? 6));

    const response = await api.get(`/company-areas?${params.toString()}`);
    return {
      data: response.data.data,
      pagination: response.data.pagination
    };
  }

  /**
   * Obtém uma área específica por ID com todos os detalhes (incluindo usuários)
   */
  async getAreaById(id: string): Promise<CompanyArea> {
    const response = await api.get(`/company-areas/${id}?includeUsers=true`);
    return response.data.data;
  }

  /**
   * Cria uma nova área da empresa
   */
  async createArea(data: CreateAreaData): Promise<CompanyArea> {
    // Normalizar payload: não enviar managerId vazio
    const payload: any = { ...data };
    if (!payload.managerId) {
      delete payload.managerId;
    }
    const response = await api.post('/company-areas', payload);
    return response.data.data;
  }

  /**
   * Atualiza uma área existente
   */
  async updateArea(id: string, data: UpdateAreaData): Promise<CompanyArea> {
    // Validar ID
    if (!id || !id.trim()) {
      throw new Error('ID da área é obrigatório');
    }

    // Normalizar payload: não enviar managerId vazio
    const payload: any = { ...data };
    if (payload.managerId === '' || payload.managerId === undefined) {
      delete payload.managerId;
    }

    // Garantir que active seja boolean se fornecido
    if (payload.active !== undefined && typeof payload.active !== 'boolean') {
      payload.active = Boolean(payload.active);
    }

    try {
      const response = await api.put(`/company-areas/${id}`, payload);
      return response.data.data;
    } catch (error: any) {
      // Log para debug
      console.error('Erro ao atualizar área:', {
        id,
        payload,
        error: error.response?.data || error.message
      });
      throw error;
    }
  }

  /**
   * Remove uma área (soft delete)
   */
  async deleteArea(id: string): Promise<void> {
    await api.delete(`/company-areas/${id}`);
  }

  /**
   * Lista usuários disponíveis para serem gestores de área
   */
  async getAvailableManagers(): Promise<AvailableManager[]> {
    const response = await api.get('/company-areas/available-managers');
    return response.data.data;
  }
}

export default new CompanyAreaService();
